/**
 * Environment Configuration System
 * Centralized configuration management for different deployment environments
 */

export type Environment = "development" | "staging" | "production";

export interface DatabaseConfig {
  url: string;
  anonKey: string;
  serviceRoleKey?: string;
  maxConnections: number;
  enableRealtime: boolean;
}

export interface EnvironmentConfig {
  environment: Environment;
  debug: boolean;
  apiUrl: string;
  appUrl: string;
  database: DatabaseConfig;
  features: {
    realtime: boolean;
    analytics: boolean;
    errorReporting: boolean;
    performanceMonitoring: boolean;
    maintenance: boolean;
  };
  limits: {
    maxFileSize: number;
    requestTimeout: number;
    retryAttempts: number;
  };
  integrations: {
    stripe?: {
      publishableKey: string;
      webhookSecret?: string;
    };
    sendgrid?: {
      apiKey: string;
      fromEmail: string;
    };
    cloudinary?: {
      cloudName: string;
      apiKey: string;
    };
  };
}

// Helper function to get environment variables with fallbacks
const getEnvVar = (key: string, defaultValue?: string): string => {
  return import.meta.env[key] || defaultValue || "";
};

// Helper function to get boolean environment variables
const getEnvBool = (key: string, defaultValue: boolean = false): boolean => {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  return value === "true" || value === "1";
};

// Environment-specific configurations - aligned with edge function URLs
const environments: Record<Environment, EnvironmentConfig> = {
  development: {
    environment: "development",
    debug: getEnvBool("VITE_DEBUG", true),
    apiUrl: "https://zsjwdvuitzhgoxdhimzr.supabase.co",
    appUrl: "https://dev-nationcard.nationcard.org", // Updated to match edge function
    database: {
      url: "https://zsjwdvuitzhgoxdhimzr.supabase.co",
      anonKey:
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpzandkdnVpdHpoZ294ZGhpbXpyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDk1NzYwOTYsImV4cCI6MjA2NTE1MjA5Nn0.nNHL4Y5pL4t7IoASa-qv10bDeR1FYa_0n0uKaBlRMwY",
      maxConnections: 10,
      enableRealtime: getEnvBool("VITE_FEATURE_REALTIME", true),
    },
    features: {
      realtime: getEnvBool("VITE_FEATURE_REALTIME", true),
      analytics: getEnvBool("VITE_FEATURE_ANALYTICS", false),
      errorReporting: getEnvBool("VITE_FEATURE_ERROR_REPORTING", true),
      performanceMonitoring: getEnvBool(
        "VITE_FEATURE_PERFORMANCE_MONITORING",
        true
      ),
      maintenance: getEnvBool("VITE_FEATURE_MAINTENANCE", false),
    },
    limits: {
      maxFileSize: 10 * 1024 * 1024, // 10MB
      requestTimeout: parseInt(getEnvVar("VITE_API_TIMEOUT", "30000")),
      retryAttempts: 3,
    },
    integrations: {},
  },
  staging: {
    environment: "staging",
    debug: getEnvBool("VITE_DEBUG", true),
    apiUrl: "https://bjqcoyyeufpdpvgrdgou.supabase.co",
    appUrl: "https://staging-nationcard.nationcard.org", // Updated to match edge function
    database: {
      url: "https://bjqcoyyeufpdpvgrdgou.supabase.co",
      anonKey:
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJqcWNveXlldWZwZHB2Z3JkZ291Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc1MTE4MDEsImV4cCI6MjA2MzA4NzgwMX0.ozI-Tc69Yi2gCh3gcm66srdRhpSmOBSpKutTq2hKzgI",
      maxConnections: 20,
      enableRealtime: getEnvBool("VITE_FEATURE_REALTIME", true),
    },
    features: {
      realtime: getEnvBool("VITE_FEATURE_REALTIME", true),
      analytics: getEnvBool("VITE_FEATURE_ANALYTICS", true),
      errorReporting: getEnvBool("VITE_FEATURE_ERROR_REPORTING", true),
      performanceMonitoring: getEnvBool(
        "VITE_FEATURE_PERFORMANCE_MONITORING",
        true
      ),
      maintenance: getEnvBool("VITE_FEATURE_MAINTENANCE", false),
    },
    limits: {
      maxFileSize: 25 * 1024 * 1024, // 25MB
      requestTimeout: parseInt(getEnvVar("VITE_API_TIMEOUT", "45000")),
      retryAttempts: 5,
    },
    integrations: {
      stripe: {
        publishableKey: "pk_test_placeholder",
      },
    },
  },
  production: {
    environment: "production",
    debug: getEnvBool("VITE_DEBUG", false),
    apiUrl: "https://buigjjovjqckdwlazhlx.supabase.co",
    appUrl: "https://nationcard.org",
    database: {
      url: "https://buigjjovjqckdwlazhlx.supabase.co",
      anonKey:
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJ1aWdqam92anFja2R3bGF6aGx4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ3NDg4MTEsImV4cCI6MjA2MDMyNDgxMX0.k7H3omf1u7wKmpDvuQ9EbE5N_m5yzdXA24AZRt2MWt0",
      maxConnections: 50,
      enableRealtime: getEnvBool("VITE_FEATURE_REALTIME", true),
    },
    features: {
      realtime: getEnvBool("VITE_FEATURE_REALTIME", true),
      analytics: getEnvBool("VITE_FEATURE_ANALYTICS", true),
      errorReporting: getEnvBool("VITE_FEATURE_ERROR_REPORTING", true),
      performanceMonitoring: getEnvBool(
        "VITE_FEATURE_PERFORMANCE_MONITORING",
        true
      ),
      maintenance: getEnvBool("VITE_FEATURE_MAINTENANCE", false),
    },
    limits: {
      maxFileSize: 50 * 1024 * 1024, // 50MB
      requestTimeout: parseInt(getEnvVar("VITE_API_TIMEOUT", "60000")),
      retryAttempts: 3,
    },
    integrations: {
      stripe: {
        publishableKey: "pk_live_placeholder",
      },
      sendgrid: {
        apiKey: "sendgrid-api-key-placeholder",
        fromEmail: "noreply@nationcard.com",
      },
    },
  },
};

/**
 * Environment Detection Strategy
 *
 * 1. Use VITE_ENVIRONMENT if set (should be set at build time in Vite config and CI/CD).
 * 2. Fallback to NODE_ENV (import.meta.env.MODE).
 * 3. As last resort, use URL/hostname detection (mainly for dev experience and for debugging).
 * 4. Emit warnings if mismatch detected between build/runtime environments.
 */
function getCurrentEnvironment(): Environment {
  // 1. Prefer explicit VITE_ENVIRONMENT from build (CI/CD + Vite config)
  const viteEnv = getEnvVar("VITE_ENVIRONMENT");
  if (viteEnv === "production") return "production";
  if (viteEnv === "staging") return "staging";
  if (viteEnv === "development") return "development";

  // 2. Fallback to Vite's MODE
  const nodeEnv = (import.meta.env.MODE as string | undefined) || "";
  if (nodeEnv === "production") return "production";
  if (nodeEnv === "development") return "development";

  // 3. Fallback to runtime URL detection (for old deployments/dev/testing only!)
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname.includes("staging-nationcard.nationcard.org"))
      return "staging";
    if (hostname.includes("dev-nationcard.nationcard.org"))
      return "development";
    if (hostname.includes("localhost") || hostname.includes("127.0.0.1"))
      return "development";
  }

  // 4. Default
  return "development";
}

export const currentEnvironment = getCurrentEnvironment();
export const config = environments[currentEnvironment];

// Helper functions
export const isProduction = () => currentEnvironment === "production";
export const isStaging = () => currentEnvironment === "staging";
export const isDevelopment = () => currentEnvironment === "development";
export const isDebugEnabled = () => config.debug;

// Feature flags
export const isFeatureEnabled = (
  feature: keyof EnvironmentConfig["features"]
) => {
  return config.features[feature];
};

// Configuration validation
export const validateConfig = (): boolean => {
  const requiredKeys = ["apiUrl", "appUrl", "database"];
  return requiredKeys.every((key) => config[key as keyof EnvironmentConfig]);
};

// Environment-specific logging
export const envLog = {
  debug: (...args: any[]) => {
    if (config.debug) {
      console.log(`[${currentEnvironment.toUpperCase()}]`, ...args);
    }
  },
  info: (...args: any[]) => {
    console.info(`[${currentEnvironment.toUpperCase()}]`, ...args);
  },
  warn: (...args: any[]) => {
    console.warn(`[${currentEnvironment.toUpperCase()}]`, ...args);
  },
  error: (...args: any[]) => {
    console.error(`[${currentEnvironment.toUpperCase()}]`, ...args);
  },
};

// --- Build/Runtime Verification Logging ---

(function logBuildAndRuntimeEnv() {
  // Log detected environment values
  const viteEnv = getEnvVar("VITE_ENVIRONMENT", "NOT_SET");
  const nodeEnv = (import.meta.env.MODE as string | undefined) || "NOT_SET";
  const urlBased =
    typeof window !== "undefined" && window.location.hostname
      ? window.location.hostname.includes("staging-nationcard.nationcard.org")
        ? "staging"
        : window.location.hostname.includes("dev-nationcard.nationcard.org")
          ? "development"
          : window.location.hostname.includes("localhost") ||
              window.location.hostname.includes("127.0.0.1")
            ? "development"
            : "production"
      : "N/A";

  // Compare build-time (VITE_ENVIRONMENT), runtime, and fallback
  let effectiveEnv = currentEnvironment;

  if (viteEnv !== "NOT_SET" && viteEnv !== effectiveEnv) {
    envLog.warn(
      `Mismatch between build-time env (VITE_ENVIRONMENT=${viteEnv}) and effective environment (${effectiveEnv}).`,
      `Build-time env should match the intended deployment!`
    );
  }
  envLog.info(`Environment boot summary:`, {
    VITE_ENVIRONMENT: viteEnv,
    NODE_ENV: nodeEnv,
    urlBased: urlBased,
    effective: effectiveEnv,
  });

  // Print critical config summary for debugging
  envLog.info(`Supabase URL:`, config.database.url);
  envLog.info(`Supabase KEY:`, config.database.anonKey.slice(0, 16) + "...");
  envLog.info(`API URL:`, config.apiUrl);
  envLog.info(`App URL:`, config.appUrl);
  envLog.info(`Debug mode:`, config.debug);
  envLog.info(`Features:`, config.features);
})();

console.log(
  `🚀 NationCard™ initialized in ${currentEnvironment.toUpperCase()} mode`
);
